import type { ReactNode } from 'react';
import { cn, Flex, Icon, type IFlexProps } from '@kognitos/lattice';
import { OutputRenderer } from './OutputRenderer';
import { ERenderMode, type TResolvedValue } from '../types';
import { isFile, isRemoteValue } from '../utils';
import { isExcelFile, isPdfFile } from './output-card-utils';

interface IFileDocumentPreviewProps {
  className?: string;
  /**
   * Inset around the silhouette. The card previews want the roomy default; a
   * denser surface such as a run-table cell can tighten it without having to
   * fight the class list.
   */
  paddingX?: IFlexProps['paddingX'];
  paddingY?: IFlexProps['paddingY'];
}

/**
 * Read-only rendered media; clicking the parent opens the full viewer.
 *
 * A document renders as a page sitting on the tile, the way a document
 * thumbnail reads anywhere else. A browser session has no page to show at this
 * size, so it states what it is with its glyph and nothing else.
 */
export function OutputMediaPreview({
  value,
  browser = false,
  className,
  silhouetteOnly = false,
}: {
  value: TResolvedValue;
  browser?: boolean;
  /** Height for the host surface; the run table and the card differ. */
  className?: string;
  /**
   * Draw the silhouette whatever the value is. A run-table cell sets this: it
   * draws one of these per row, so a real renderer there is a base64 decode
   * and a pdf.js instance per row (KOG-11945).
   */
  silhouetteOnly?: boolean;
}) {
  // `silhouetteOnly` is how a caller declares it cannot afford a real render
  // (a run-table cell, one per row — KOG-11945); a single card or run-log
  // output can, and FileRenderer already gates remote downloads by size and
  // caches them. An unresolved remote-value wrapper has no file to render
  // regardless of the caller, so it always falls back to the silhouette.
  const showSilhouette = silhouetteOnly || isRemoteValue(value);
  // Only a PDF opens as a page; drawing a page for a text file promised a
  // viewer that the full preview never shows.
  const paged = isFile(value) && isPdfFile(value);
  // A workbook previews as a table that runs edge to edge, like the other card tables.
  const flush = isFile(value) && isExcelFile(value);

  if (!browser && !paged) {
    return (
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-lg',
          className ?? 'h-24',
        )}
      >
        {showSilhouette ? (
          <FileDocumentPreview className="h-full rounded-none border-0 bg-transparent" />
        ) : (
          <Flex
            align="stretch"
            width="full"
            height="full"
            // The tile is the only frame; a code block's own border, fill and
            // scrollbar would draw a second one inside it.
            className={cn(
              'pointer-events-none [&_pre]:overflow-hidden [&_pre]:rounded-none [&_pre]:border-0 [&_pre]:bg-transparent [&_pre]:p-0',
              !flush && 'p-3',
            )}
            inert
          >
            {/* A fixed-height tile: the file fills it instead of a taller frame the tile crops. */}
            <OutputRenderer
              value={value}
              mode={ERenderMode.PREVIEW}
              fillContainer
            />
          </Flex>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        // No fill or edge here: the hosting surface already draws both; doubling up read as a tile inside a tile.
        'relative w-full overflow-hidden rounded-lg',
        className ?? 'h-24',
      )}
    >
      {browser ? (
        <Flex align="center" justify="center" className="h-full w-full">
          <Icon type="Globe" size="sm" className="text-muted-foreground" />
        </Flex>
      ) : (
        <DocumentPageSheet>
          {showSilhouette ? (
            <FileDocumentPreview className="h-full rounded-none border-0" />
          ) : (
            <MiniaturePage>
              <OutputRenderer value={value} mode={ERenderMode.PREVIEW} />
            </MiniaturePage>
          )}
        </DocumentPageSheet>
      )}
    </div>
  );
}

/**
 * A page sitting on a tile, running off the bottom like the top of a document.
 * `overlay` is drawn over the sheet, unscaled.
 */
export function DocumentPageSheet({
  children,
  overlay,
}: {
  children: ReactNode;
  overlay?: ReactNode;
}) {
  return (
    <Flex
      align="start"
      justify="center"
      width="full"
      height="full"
      paddingX={2}
      className="pt-2"
    >
      {/* Its edge is a hairline ring on the theme's border token, so nothing
          is inset and nothing is hardcoded. */}
      <div
        data-testid="output-document-page"
        className="relative aspect-[1/1.3] w-3/4 shrink-0 overflow-hidden rounded-t-sm bg-background ring-1 ring-border"
      >
        {children}
        {overlay}
      </div>
    </Flex>
  );
}

/**
 * Lays content out at twice the sheet and scales it down, so it reads as a
 * miniature page rather than a cropped one.
 */
export function MiniaturePage({ children }: { children: ReactNode }) {
  return (
    <Flex
      align="stretch"
      className="pointer-events-none h-[200%] w-[200%] origin-top-left scale-50 [&_pre]:overflow-hidden [&_pre]:rounded-none [&_pre]:border-0 [&_pre]:bg-transparent"
      inert
    >
      {/* Stretching the renderer to the sheet is what makes its `h-full` resolve; left auto-height, every `h-full` below this collapses. */}
      {children}
    </Flex>
  );
}

/**
 * Lightweight document silhouette for surfaces that must not render file bytes.
 * It lives outside `OutputCardPreview` because two modules need it — the card
 * previews and the run table — while the other previews there are private to
 * the card.
 */
export function FileDocumentPreview({
  className,
  paddingX = 3,
  paddingY = 2.5,
}: IFileDocumentPreviewProps) {
  return (
    <Flex
      direction="column"
      justify="between"
      paddingX={paddingX}
      paddingY={paddingY}
      className={cn(
        'min-h-full rounded-md border border-border bg-background',
        className,
      )}
    >
      <Flex align="baseline" justify="between" width="full" gap={2}>
        <span className="h-1 w-2/5 bg-foreground/25" />
        <span className="h-1 w-1/4 bg-foreground/10" />
      </Flex>
      <Flex direction="column" width="full" gap={1.5}>
        <span className="h-0.5 w-1/2 bg-foreground/20" />
        <span className="h-0.5 w-full bg-foreground/10" />
        <span className="h-0.5 w-5/6 bg-foreground/10" />
      </Flex>
      <Flex justify="end" width="full" gap={2}>
        <span className="h-1 w-1/4 bg-foreground/10" />
        <span className="h-1 w-1/5 bg-foreground/25" />
      </Flex>
    </Flex>
  );
}
